import { Check } from '@phosphor-icons/react';
import {
  Button,
  cn,
  Field,
  Link,
  Modal,
  ModalClose,
  ModalContent,
  Stack,
  TagInput,
  Well,
} from '@reelplicate/ui';
import { useId, useState } from 'react';
import { Link as RouterLink } from 'react-router';

import { isEmail } from '@/features/onboarding/schemas';

import { ASSIGNABLE_ROLES, type Role } from './roles';

/**
 * Figma "Team — Invite people" (328:2662): a Medium Modal with a Tag Input
 * of emails, a Role picker (Role Options, 107:105) and - when every seat is
 * taken - a warning that there's no room for more people until an upgrade.
 * "Send N invites" counts the emails. `onSend` resolves true once they're
 * sent; the modal keeps the emails when it fails.
 */
export function InviteModal({
  open,
  onOpenChange,
  seatsUsed,
  seatLimit,
  onSend,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  seatsUsed: number;
  /** The plan's seats; unknown while it loads. */
  seatLimit: number | undefined;
  onSend: (emails: string[], role: Role) => Promise<boolean>;
}) {
  const [emails, setEmails] = useState<string[]>([]);
  const [role, setRole] = useState<Role>('editor');
  const [error, setError] = useState<string>();
  const [sending, setSending] = useState(false);
  const roleLabelId = useId();
  const full = seatLimit !== undefined && seatsUsed >= seatLimit;

  const reset = () => {
    setEmails([]);
    setRole('editor');
    setError(undefined);
  };

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <ModalContent
        title="Invite people"
        description="Paste one or more emails. They’ll get a link that expires in 7 days."
        footerStart={
          <Link asChild size="sm">
            <RouterLink to="/settings/billing">Compare plans</RouterLink>
          </Link>
        }
        footer={
          <>
            <ModalClose asChild>
              <Button variant="ghost">Cancel</Button>
            </ModalClose>
            <Button
              disabled={emails.length === 0}
              loading={sending}
              onClick={async () => {
                setSending(true);
                const sent = await onSend(emails, role);
                setSending(false);
                if (sent) reset();
              }}
            >
              {emails.length > 1 ? `Send ${emails.length} invites` : 'Send invite'}
            </Button>
          </>
        }
      >
        <Stack gap={5}>
          <Field label="Emails" error={error}>
            <TagInput
              value={emails}
              onValueChange={(next) => {
                setEmails(next);
                setError(undefined);
              }}
              validate={isEmail}
              onReject={(tag, reason) =>
                setError(
                  reason === 'duplicate'
                    ? `${tag} is already on the list`
                    : `${tag} isn’t an email address`,
                )
              }
              placeholder="name@company.com"
            />
          </Field>

          <Stack gap={1.5}>
            <p id={roleLabelId} className="text-label-md text-text-primary">
              Role
            </p>
            <Stack
              role="radiogroup"
              aria-labelledby={roleLabelId}
              gap={0.5}
              className="border-border-subtle rounded-md border p-1"
            >
              {ASSIGNABLE_ROLES.map((option) => {
                const selected = option.id === role;
                return (
                  <label
                    key={option.id}
                    className={cn(
                      'flex cursor-pointer items-start gap-2.5 rounded-sm px-2.5 py-2',
                      'hover:bg-bg-muted has-[input:focus-visible]:ring-border-focus has-[input:focus-visible]:ring-2',
                      selected && 'bg-bg-subtle',
                    )}
                  >
                    <input
                      type="radio"
                      name="invite-role"
                      value={option.id}
                      checked={selected}
                      onChange={() => setRole(option.id)}
                      className="sr-only"
                    />
                    <Stack gap={0.5} className="min-w-0 flex-1">
                      <span className="text-body-md-strong text-text-primary">
                        {option.name}
                      </span>
                      <span className="text-caption text-text-secondary">
                        {option.description}
                      </span>
                    </Stack>
                    {selected && (
                      <Check
                        aria-hidden
                        className="text-icon-brand mt-0.5 size-4 shrink-0"
                      />
                    )}
                  </label>
                );
              })}
            </Stack>
          </Stack>

          {full && (
            <Well
              tone="warning"
              title={`You’re using ${seatsUsed} of ${seatLimit} seats`}
            >
              Every seat on your plan is taken by a member or a pending invite. Upgrade,
              remove someone or revoke an invite to make room.
            </Well>
          )}
        </Stack>
      </ModalContent>
    </Modal>
  );
}
